import { createSelector } from "reselect"
import { get, chain, find, pickBy, merge, filter } from "lodash"
import { getFavoriteFactors } from "modules/portfolio-root/selectors/portfolios"
import { getModelCategories } from "selectors/modelMetadata"
import { getAllItems, getOrderedCategoryItems } from "./items"
import { getPerformanceHistory, getPerformanceAttributionData } from "./performance"
import { COLORS, ANALYZE_FACTORS_TAKE, MANDATORY_PERFORMANCE_METRICS } from "helpers/analyzeConstants"
import { computePerformanceData, computeNonZerosKeys } from "helpers/analyze"

const getCategory = state => get(state, "modules.securityProfilePerformance.displayType")

const getData = createSelector(
  [getAllItems, getPerformanceHistory],
  computePerformanceData,
)

const getOverrideKeys = createSelector(
  [getFavoriteFactors],
  (favoriteFactors) => merge(MANDATORY_PERFORMANCE_METRICS, favoriteFactors),
)

const getNonZerosKeys = createSelector(
  [getData, getOverrideKeys],
  computeNonZerosKeys,
)

export const getFilteredData = createSelector(
  [getData, getNonZerosKeys],
  (data, keys) => data.map(item => pickBy(item, (item, key) => keys[key])),
)

export const computeCategoryItems = (items, keys) => filter(items, item => keys[item.id])

// Security profile attribution panel needs all factors
// Analyze and security profile graphs only want subset (based on number of colors)
export const computeCategoryItemsSubset = (items, keys) => chain(items)
  .thru(items => computeCategoryItems(items, keys))
  .take(ANALYZE_FACTORS_TAKE + 1) // 1 category total item + X items)
  .map((item, index) => ({
    ...item,
    color: item.color || COLORS[index - 1], // Ignore to the total item color
  }))
  .value()

export const getCategoryItemsSubset = createSelector(
  [getOrderedCategoryItems, getNonZerosKeys],
  computeCategoryItemsSubset,
)

export const getCategoryItems = createSelector(
  [getOrderedCategoryItems, getNonZerosKeys],
  computeCategoryItems,
)

export const getSingleItemCategories = createSelector(
  [getAllItems, getNonZerosKeys, getModelCategories],
  (items, keys, categories) =>
    chain(items)
      .filter(item => keys[item.id])
      .groupBy(item => item.category)
      .pickBy(category => category.length === 2 && !find(categories, c => c.id === category[0].category).singleFactor)
      .mapValues(category => category[1].label)
      .value(),
)

export const getDisplayCategories = createSelector(
  [getAllItems, getNonZerosKeys],
  (items, keys) =>
    chain(items)
      .filter(item => keys[item.id])
      .map(item => item.category)
      .filter(item => !!item)
      .countBy(item => item)
      .omitBy(count => count <= 2)
      .value(),
)

export const getPerformanceAttribution = createSelector(
  [getPerformanceAttributionData, getCategory, getAllItems],
  (attribution, category, items) => {
    const attributionCategoryLabel = (category === "total" || category === "factors") ? "Total" : get(find(items, { id: category }), "label")
    return {
      category: `${attributionCategoryLabel} Performance Attribution`,
      ...attribution,
    }
  },
)
